import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ChatUserSettingsResponse, UpdateChatUserSettingsInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const DEFAULT_SETTINGS: ChatUserSettingsResponse = {
  userId: '',
  displayName: '',
  bio: '',
  bannerColor: '#cbbfb5',
  avatarColor: '#5865F2',
  avatarImageId: null,
  notificationNewMessages: 'all',
  notificationReactions: true,
  notificationChannelAdded: true,
  notificationSoundDMs: true,
  notificationSoundChannels: false,
  appearanceShowEmojis: true,
  appearanceShowReactions: true,
  appearanceCompactMode: false,
  appearanceAccentColor: '#5865F2',
  appearanceChannelListView: 'unified' as const,
  backgroundImageId: null,
  backgroundColor: '',
  backgroundBlur: false,
  privacyWhoCanDM: 'everyone',
  privacyReadReceipts: true,
  privacyShowOnlineStatus: true,
  createdAt: '',
  updatedAt: '',
};

interface UseChatSettingsReturn {
  settings: ChatUserSettingsResponse;
  loading: boolean;
  error: string | null;
  saveSettings: (input: UpdateChatUserSettingsInput) => Promise<ChatUserSettingsResponse>;
}

export function useChatSettings(): UseChatSettingsReturn {
  const queryClient = useQueryClient();
  const settingsKey = queryKeys.chat.settings();

  const { data, isLoading, error } = useQuery({
    queryKey: settingsKey,
    queryFn: () => api.getChatSettings(),
  });

  const mutation = useMutation({
    mutationFn: (input: UpdateChatUserSettingsInput) => api.updateChatSettings(input),
    onSuccess: (result) => {
      queryClient.setQueryData(settingsKey, result);
    },
  });

  return {
    settings: data?.settings ?? DEFAULT_SETTINGS,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load settings' : null,
    saveSettings: async (input) => {
      const result = await mutation.mutateAsync(input);
      return result.settings;
    },
  };
}
